import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useDropzone } from "react-dropzone";
import {
  Box,
  Typography,
  Button,
  TextField,
  Modal,
  CircularProgress
} from "@mui/material";
import { API_BASE_URL } from "../../../utils/api";

const ArchitecturalRequestComments = ({ architectural_request_id, currentUserId, refreshTrigger }) => {
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [filePreview, setFilePreview] = useState(null);
  const [fileError, setFileError] = useState("");
  const [uploadedFiles, setUploadedFiles] = useState([]);



  const fetchComments = useCallback(async () => {
    if (!architectural_request_id) return;
    setIsLoading(true);
    try {
      const response = await axios.get(
        `${API_BASE_URL}/getExternalArchitecturalComments/${architectural_request_id}`
      );
      setComments(response.data);
    } catch (error) {
      console.error("Failed to fetch comments:", error);
    } finally {
      setIsLoading(false);
    }
  }, [architectural_request_id]);


  useEffect(() => {
    fetchComments();
  }, [fetchComments]);
  

 

  useEffect(() => {
    const fetchFiles = async () => {
      try {
        const res = await axios.get(`${API_BASE_URL}/request-files/${architectural_request_id}`);
        setUploadedFiles(res.data);
      } catch (error) {
        console.error("Failed to fetch files:", error);
      }
    };

    if (architectural_request_id) {
      fetchFiles();
    }
  }, [architectural_request_id, refreshTrigger]);

  const onDrop = useCallback((acceptedFiles) => {
    if (acceptedFiles.length > 0) {
      const file = acceptedFiles[0];
      setSelectedFile(file);
      setFilePreview(file.type.startsWith("image") ? URL.createObjectURL(file) : null);
      setFileError("");
    }
  }, []);

  const onDropRejected = () => {
    setFileError("Only PDF and DOCX files are allowed.");
  };

  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    onDropRejected,
    accept: {
      "application/pdf": [".pdf"],
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document": [".docx"],
    },
    multiple: false,
  });

  const uploadFileToServer = async (commentId) => {
    if (!selectedFile) return null;

    const formData = new FormData();
    formData.append("request_id", architectural_request_id);
    formData.append("upload_urls", selectedFile);
    formData.append("uploaded_by", currentUserId);
    formData.append("comment_id", commentId);

    try {
      await axios.post(`${API_BASE_URL}/request-files`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      return true;
    } catch (error) {
      console.error("Failed to upload file:", error);
      return false;
    }
  };
  const handleAddComment = async () => {
    if (!newComment.trim()) {
      alert("Comment cannot be empty.");
      return;
    }
  
    if (fileError) {
      alert("Please resolve the file error before submitting.");
      return;
    }
  
    try {
      const commentData = {
        architectural_request_id,
        user_id: currentUserId,
        comment_text: newComment,
        is_internal: 0,
      };
  
      const commentResponse = await axios.post(`${API_BASE_URL}/addArchitecturalRequestComments`, commentData);
      const newCommentId = commentResponse.data.comment_id;
  
      if (selectedFile) {
        const success = await uploadFileToServer(newCommentId);
        if (!success) {
          alert("Comment saved, but file upload failed.");
        }
      }
  
      await fetchComments(); // ✅ Refresh the comment list
      setNewComment("");
      setSelectedFile(null);
      setFilePreview(null);
      setFileError("");
      setIsModalOpen(false);
    } catch (error) {
      console.error("Failed to add comment:", error);
      alert("Failed to add comment. Please try again.");
    }
  };
  

  const getFilesForComment = (commentId) => {
    return uploadedFiles.filter((file) => file.comment_id === commentId);
  };

  if (isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", marginTop: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 2 }}>Comments</Typography>
      {comments.length > 0 ? (
        comments.map((comment, index) => {
          const commentFiles = getFilesForComment(comment.id || comment.comment_id);
          const [datePart, timePart] = comment.sent_at.split("T");
          const [hour, minute] = timePart.split(":");
          return (
            <Box
              key={index}
              sx={{
                mb: 2,
                p: 2,
                border: "1px solid #ddd",
                borderRadius: "8px",
                backgroundColor: "#e3f2fd",
              }}
            >
              <Typography variant="body2" sx={{ mb: 1, fontWeight: "bold", color: "blue" }}>
                Comment:
              </Typography>
              <Typography variant="body2" sx={{ mb: 1 }}>{comment.comment_text}</Typography>

              {commentFiles.length > 0 && (
                <Box sx={{ mt: 1 }}>
                  <Typography variant="body2" fontWeight="bold">📎 Attached File(s):</Typography>
                  {commentFiles.map((file, idx) => (
                    <Box key={idx}>
                    <a
                      href={`${API_BASE_URL}${file.file_url}`}
                      download={file.file_name}
                      style={{ textDecoration: "none", color: "#1976d2" }}
                    >
                      {file.file_name}
                    </a>
                  </Box>
                  
                  ))}
                </Box>
              )}

              <Typography
                variant="caption"
                sx={{
                  fontSize: "0.75rem",
                  color: "text.secondary",
                  fontStyle: "italic",
                  display: "block",
                  mt: 1,
                }}
              >
                🕒 Added by {comment.user_name} {comment.user_lastname || ""} on {`${datePart} at ${hour}:${minute}`}
              </Typography>
            </Box>
          );
        })
      ) : (
        <Typography>No comments available.</Typography>
      )}

      <Button variant="contained" color="primary" onClick={() => setIsModalOpen(true)}>
        Add Comment
      </Button>

      <Modal open={isModalOpen} onClose={() => setIsModalOpen(false)}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: 400,
            backgroundColor: "white",
            padding: 4,
            borderRadius: 2,
            boxShadow: 24,
          }}
        >
          <Typography variant="h6" sx={{ mb: 2 }}>Add Comment</Typography>
          <TextField
            fullWidth
            label="Comment"
            multiline
            rows={3}
            value={newComment}
            onChange={(e) => setNewComment(e.target.value)}
            sx={{ mb: 2 }}
          />

          <Box {...getRootProps()} sx={{
            border: "2px dashed #ddd",
            padding: "20px",
            textAlign: "center",
            cursor: "pointer",
            backgroundColor: "#f9f9f9",
            mb: 2,
          }}>
            <input {...getInputProps()} />
            <Typography>Drag & Drop a PDF or DOCX file here, or click to select one</Typography>
          </Box>

          {fileError && <Typography sx={{ color: "red", fontSize: 14 }}>{fileError}</Typography>}
          {filePreview && (
            <Box sx={{ textAlign: "center", mt: 2 }}>
              <Typography variant="body2">📎 File Preview:</Typography>
              <img src={filePreview} alt="Preview" style={{ maxWidth: "100%", maxHeight: 200 }} />
            </Box>
          )}

          <Button fullWidth variant="contained" color="primary" onClick={handleAddComment} sx={{ mt: 2 }}>
            Save Comment
          </Button>
        </Box>
      </Modal>
    </Box>
  );
};

export default ArchitecturalRequestComments;
